import type { InstrumentId, InstrumentProfile } from './types';
import { OCARINA_VARIANTS } from './ocarina';

/**
 * Instrument profiles. Ukulele is the primary instrument and is listed first
 * everywhere the order is user-visible.
 */

export const UKULELE: InstrumentProfile = {
  id: 'ukulele',
  name: 'Ukulele',
  family: 'fretted',
  character: 'sol',
  sound: 'ukulele',
  clef: 'treble',
  writtenOctaveOffset: 0,
  polyphonic: true,
  // With the standard re-entrant tuning the lowest sounding note is C4, not the
  // G below it: string 4 is tuned above strings 3 and 2.
  range: { low: 60, high: 84 },
  comfortable: { low: 60, high: 79 },
  tunings: [
    {
      id: 'uke.gcea-high',
      name: 'Standard (high G)',
      midis: [67, 60, 64, 69],
      reentrant: true,
      frets: 15,
    },
    { id: 'uke.gcea-low', name: 'Low G', midis: [55, 60, 64, 69], frets: 15 },
    {
      id: 'uke.adfsb',
      name: 'D tuning (ADF♯B)',
      midis: [69, 62, 66, 71],
      reentrant: true,
      frets: 15,
    },
    { id: 'uke.baritone', name: 'Baritone (DGBE)', midis: [50, 55, 59, 64], frets: 18 },
  ],
  defaultTuningId: 'uke.gcea-high',
  notationModes: ['staff', 'tab', 'letters', 'chordDiagram'],
  widgets: ['fretboard', 'chordDiagram', 'keyboard'],
  detection: {
    windowSize: 2048,
    hopSize: 512,
    clarityThreshold: 0.88,
    minFreq: 180,
    maxFreq: 1400,
    noteOnFrames: 3,
    noteOffFrames: 6,
    centsTolerance: 35,
    chordMode: 'voicing',
  },
  hints: { default: 'tab', fadeAtMastery: 0.7 },
};

export const GUITAR: InstrumentProfile = {
  id: 'guitar',
  name: 'Guitar',
  family: 'fretted',
  character: 'volt',
  sound: 'guitar-steel',
  clef: 'treble8vb',
  writtenOctaveOffset: -1,
  polyphonic: true,
  range: { low: 40, high: 88 },
  comfortable: { low: 40, high: 76 },
  tunings: [
    {
      id: 'guitar.standard',
      name: 'Standard (EADGBE)',
      midis: [40, 45, 50, 55, 59, 64],
      frets: 20,
    },
    { id: 'guitar.drop-d', name: 'Drop D', midis: [38, 45, 50, 55, 59, 64], frets: 20 },
    { id: 'guitar.half-down', name: 'Half step down', midis: [39, 44, 49, 54, 58, 63], frets: 20 },
    { id: 'guitar.dadgad', name: 'DADGAD', midis: [38, 45, 50, 55, 57, 62], frets: 20 },
    { id: 'guitar.open-g', name: 'Open G', midis: [38, 43, 50, 55, 59, 62], frets: 20 },
  ],
  defaultTuningId: 'guitar.standard',
  notationModes: ['staff', 'tab', 'letters', 'chordDiagram'],
  widgets: ['fretboard', 'chordDiagram', 'keyboard'],
  detection: {
    windowSize: 4096,
    hopSize: 512,
    clarityThreshold: 0.9,
    minFreq: 70,
    maxFreq: 1400,
    noteOnFrames: 3,
    noteOffFrames: 8,
    centsTolerance: 35,
    chordMode: 'voicing',
  },
  hints: { default: 'tab', fadeAtMastery: 0.7 },
};

export const OCARINA: InstrumentProfile = {
  id: 'ocarina',
  name: 'Ocarina',
  family: 'wind',
  character: 'luna',
  sound: 'ocarina',
  clef: 'treble',
  writtenOctaveOffset: 0,
  polyphonic: false,
  range: { low: 69, high: 89 },
  comfortable: { low: 72, high: 86 },
  variants: OCARINA_VARIANTS,
  defaultVariantId: 'ac12',
  notationModes: ['staff', 'fingering', 'letters'],
  widgets: ['ocarina', 'keyboard'],
  detection: {
    windowSize: 2048,
    hopSize: 256,
    clarityThreshold: 0.92,
    minFreq: 200,
    maxFreq: 3000,
    noteOnFrames: 4,
    noteOffFrames: 5,
    centsTolerance: 30,
    chordMode: 'none',
  },
  hints: { default: 'fingering', fadeAtMastery: 0.75 },
};

export const PIANO: InstrumentProfile = {
  id: 'piano',
  name: 'Piano',
  family: 'keyboard',
  character: 'fifi',
  sound: 'piano',
  clef: 'grand',
  writtenOctaveOffset: 0,
  polyphonic: true,
  range: { low: 21, high: 108 },
  comfortable: { low: 48, high: 84 },
  notationModes: ['staff', 'letters'],
  widgets: ['keyboard'],
  detection: {
    windowSize: 4096,
    hopSize: 512,
    clarityThreshold: 0.9,
    minFreq: 27,
    maxFreq: 4200,
    noteOnFrames: 3,
    noteOffFrames: 6,
    centsTolerance: 30,
    chordMode: 'chroma',
  },
  hints: { default: 'none', fadeAtMastery: 0.6 },
};

export const VOICE: InstrumentProfile = {
  id: 'voice',
  name: 'Voice',
  family: 'voice',
  character: 'clara',
  sound: 'choir',
  clef: 'treble',
  writtenOctaveOffset: 0,
  polyphonic: false,
  range: { low: 48, high: 81 },
  comfortable: { low: 55, high: 76 },
  notationModes: ['staff', 'letters'],
  widgets: ['keyboard'],
  detection: {
    windowSize: 2048,
    hopSize: 256,
    clarityThreshold: 0.85,
    minFreq: 80,
    maxFreq: 1100,
    noteOnFrames: 4,
    noteOffFrames: 6,
    // Vibrato makes a sung note wander; average over a longer window.
    centsTolerance: 50,
    chordMode: 'none',
  },
  hints: { default: 'none', fadeAtMastery: 0.6 },
};

export const PROFILES: Record<InstrumentId, InstrumentProfile> = {
  ukulele: UKULELE,
  guitar: GUITAR,
  ocarina: OCARINA,
  piano: PIANO,
  voice: VOICE,
};

/** Display order: the owner's primary instrument first. */
export const INSTRUMENT_ORDER: InstrumentId[] = ['ukulele', 'guitar', 'ocarina', 'piano', 'voice'];

export function getProfile(id: InstrumentId): InstrumentProfile {
  const profile = PROFILES[id];
  if (profile === undefined) throw new Error(`Unknown instrument: ${id}`);
  return profile;
}
